import { useChatComposerStore } from '@air/feature-ag-ui-chat/composer-store';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type CanvasNullStateOutcomeAction } from '../components/CanvasNullState/canvasNullStateOutcomes';
import { openChatForCanvasPrompt, stageCanvasNodesPromptMentions } from './openChatForCanvasNodesPrompt';
import { waitForComposerReady } from './waitForComposerReady';

const submitStagedComposerMessage = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => {
      useChatComposerStore.getState().submitMessage?.();
      resolve();
    });
  });

/**
 * Opens chat with an @-mention per node, stages a slash skill or canned prompt, then auto-sends.
 * `nodeIds[0]` is the primary node (first mention); the action applies to all of them.
 */
export const openChatForCanvasNodeOutcome = async (
  nodeIds: readonly string[],
  action: CanvasNullStateOutcomeAction,
) => {
  // Open → await → stage, in that order: the composer only mounts inside an open sidebar, and its
  // pre-mount queue holds one token, so staging early collapses every mention past the first.
  openChatForCanvasPrompt();

  const ready = await waitForComposerReady();

  if (!ready) {
    // The outcome is dropped entirely here — silent in the UI, so it needs to be loud in telemetry.
    reportErrorToBugsnag({
      context: 'openChatForCanvasNodeOutcome: composer never became ready, outcome dropped',
      error: new Error('Canvas outcome dropped before composer mount'),
      metadata: { actionType: action.type, nodeCount: nodeIds.length },
    });

    return;
  }

  const composerStore = useChatComposerStore.getState();
  // An unsent draft must not ride along on an auto-send: stage the outcome after it and leave
  // the submit to the user, so their draft stays visible and intact.
  const hadDraft = composerStore.hasDraftText?.() ?? false;

  // Every staging insert lands at the caret, which can sit mid-draft; move it to the end
  // first so the staged mention and skill go after the user's text instead of splicing it.
  if (hadDraft) {
    composerStore.moveCaretToEnd?.();
  }

  stageCanvasNodesPromptMentions(nodeIds);

  if (action.type === 'slash-skill') {
    if (action.leadingMessage) {
      composerStore.appendMessageText?.(`${action.leadingMessage} `);
    }

    composerStore.insertSkill?.(action.slashCommand);

    if (action.instructions || action.skipComposerVariantCount) {
      const pendingSkill = useChatComposerStore
        .getState()
        .pendingSkillInstructions.find((entry) => entry.slashCommand === action.slashCommand);

      if (pendingSkill) {
        composerStore.addPendingSkillInstructions({
          ...pendingSkill,
          instructions: action.instructions
            ? `${pendingSkill.instructions}\n\n${action.instructions}`
            : pendingSkill.instructions,
          skipComposerVariantCount: action.skipComposerVariantCount ?? pendingSkill.skipComposerVariantCount,
        });
      } else if (action.instructions) {
        composerStore.addPendingSkillInstructions({
          id: action.slashCommand,
          instructions: action.instructions,
          skipComposerVariantCount: action.skipComposerVariantCount,
          slashCommand: action.slashCommand,
        });
      }
    }

    if (action.message) {
      composerStore.appendMessageText?.(action.message);
    }

    if (!hadDraft) {
      await submitStagedComposerMessage();
    }

    return;
  }

  if (action.type === 'chat-message') {
    composerStore.appendMessageText?.(action.message);

    if (!hadDraft && action.autoSubmit !== false) {
      await submitStagedComposerMessage();
    }
  }
};
